import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: '',
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.nome || !formData.email || !formData.telefone || !formData.mensagem) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    // Mock submission - in production, this would send to backend
    console.log('Form submitted:', formData);
    toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');

    // Reset form
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      mensagem: '',
    });
  };

  const contactInfo = [
    {
      icon: 'fa-phone',
      title: 'Telefone',
      value: '(22) 99834-2742',
      link: 'tel:+5522998342742',
      isExternal: false,
    },
    {
      icon: 'fa-envelope',
      title: 'E-mail',
      value: 'godoyaguiarsda@gmail.com',
      link: 'mailto:godoyaguiarsda@gmail.com',
      isExternal: false,
    },
  ];

  const officeLocations = [
    {
      city: 'Cabo Frio/RJ',
      address: 'Avenida Independência, n° 32, 2° andar, sala 15',
      neighborhood: 'Unamar, Cabo Frio/RJ',
      cep: 'CEP: 28928-542',
      mapLink: 'https://www.google.com/maps/search/?api=1&query=Avenida+Independência,+32,+2°+andar,+sala+15,+Unamar,+Cabo+Frio,+RJ,+28928-542',
    },
    {
      city: 'Rio de Janeiro/RJ',
      address: 'Estrada do Piaí, n° 5521, loja',
      neighborhood: 'Sepetiba, Rio de Janeiro/RJ',
      cep: 'CEP: 23530-661',
      mapLink: 'https://www.google.com/maps/search/?api=1&query=Estrada+do+Piaí,+5521,+loja,+Sepetiba,+Rio+de+Janeiro,+RJ,+23530-661',
    },
  ];

  const openingHours = [
    { day: 'Segunda a Sexta', hours: '08:00 - 18:00' },
    { day: 'Sábado', hours: '09:00 - 13:00' },
    { day: 'Domingo', hours: 'Fechado' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgba(45, 66, 56, 0.9) 0%, rgba(45, 66, 56, 0.8) 100%), url(https://images.unsplash.com/photo-1423666639041-f56000c27a9a?q=80&w=2074)',
        }}
      >
        <div className="container-padding mx-auto text-center relative z-10">
          <div className="max-w-3xl mx-auto space-y-4 slide-up">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground">
              Fale Conosco
            </h1>
            <p className="text-lg sm:text-xl text-primary-foreground/90">
              Clique no botão abaixo para iniciar uma conversa. Preencha o formulário e nossa
              equipe entrará em contato.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-padding bg-background">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
            {contactInfo.map((info, index) => (
              <Card
                key={index}
                className="card-hover border-border text-center group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-16 h-16 bg-accent-light rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                    <i className={`fas ${info.icon} text-2xl text-accent`}></i>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground">
                    {info.title}
                  </h3>
                  <a
                    href={info.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-accent transition-colors"
                  >
                    {info.value}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Office Locations */}
          <div className="mb-12">
            <h3 className="font-display text-2xl font-bold text-primary text-center mb-8">
              <i className="fas fa-map-marker-alt text-accent mr-2"></i>
              Nossos Escritórios
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {officeLocations.map((location, index) => (
                <Card
                  key={index}
                  className="card-hover border-border group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-16 h-16 bg-accent-light rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="fas fa-building text-2xl text-accent"></i>
                    </div>
                    <h4 className="font-display text-xl font-semibold text-foreground">
                      {location.city}
                    </h4>
                    <div className="text-muted-foreground text-sm space-y-1">
                      <p>{location.address}</p>
                      <p>{location.neighborhood}</p>
                      <p className="font-semibold">{location.cep}</p>
                    </div>
                    <a
                      href={location.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-accent hover:text-accent-hover font-semibold transition-colors"
                    >
                      <i className="fas fa-map-marked-alt mr-2"></i>
                      Ver no Google Maps
                      <i className="fas fa-external-link-alt ml-2 text-xs"></i>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="slide-in-left">
              <Card className="card-hover border-border">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="font-display text-2xl font-bold text-foreground mb-6">
                    <i className="fas fa-paper-plane text-accent mr-2"></i>
                    Envie sua Mensagem
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome</Label>
                      <Input
                        id="nome"
                        placeholder="Seu nome completo"
                        value={formData.nome}
                        onChange={(e) => handleInputChange('nome', e.target.value)}
                        className="border-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="seuemail@exemplo.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="border-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        type="tel"
                        placeholder="(XX) XXXXX-XXXX"
                        value={formData.telefone}
                        onChange={(e) => handleInputChange('telefone', e.target.value)}
                        className="border-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mensagem">Mensagem</Label>
                      <Textarea
                        id="mensagem"
                        placeholder="Escreva sua mensagem aqui..."
                        rows={5}
                        value={formData.mensagem}
                        onChange={(e) => handleInputChange('mensagem', e.target.value)}
                        className="border-input resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-accent hover:bg-accent-hover text-accent-foreground font-semibold shadow-lg"
                    >
                      <i className="fas fa-paper-plane mr-2"></i>
                      Enviar Mensagem
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Additional Info */}
            <div className="space-y-8 slide-in-right">
              {/* WhatsApp CTA */}
              <Card className="card-hover border-accent/50 bg-accent-light">
                <CardContent className="p-6 sm:p-8 text-center">
                  <i className="fab fa-whatsapp text-[#25D366] text-6xl mb-4"></i>
                  <h3 className="font-display text-2xl font-bold text-foreground mb-3">
                    Fale no WhatsApp
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Clique no botão abaixo para iniciar uma conversa e nossa equipe entrará em
                    contato.
                  </p>
                  <Button
                    asChild
                    size="lg"
                    className="bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold shadow-lg"
                  >
                    <a
                      href="https://wa.me/5522998342742?text=Ol%C3%A1!%20Gostaria%20de%20falar%20com%20um%20especialista"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-whatsapp mr-2 text-xl"></i>
                      Fale no WhatsApp
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Opening Hours */}
              <Card className="card-hover border-border">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="font-display text-2xl font-bold text-foreground mb-6">
                    <i className="far fa-clock text-accent mr-2"></i>
                    Horário de Atendimento
                  </h3>
                  <div className="space-y-4">
                    {openingHours.map((schedule, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center pb-3 border-b border-border last:border-0"
                      >
                        <span className="text-muted-foreground">{schedule.day}</span>
                        <span className="font-semibold text-foreground">{schedule.hours}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Map Placeholder */}
              <Card className="card-hover border-border overflow-hidden">
                <div className="relative h-64 bg-muted">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3535.7052489171865!2d-48.54926768493168!3d-27.596377982840643!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjfCsDM1JzQ2LjkiUyA0OMKwMzInNDguMiJX!5e0!3m2!1spt-BR!2sbr!4v1234567890123!5m2!1spt-BR!2sbr"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização do escritório"
                  ></iframe>
                </div>
                <CardContent className="p-4 text-center">
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                  >
                    <a
                      href="https://maps.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-map-marked-alt mr-2"></i>
                      Ver no Mapa
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;